/**
 * プロセスモデル固有の知識をここ 1 箇所に閉じ込める。
 *
 * SpiffArena の API は業務データを返さないため、「差し戻しかどうか」「件名は何か」を
 * 知るには process instance metadata（`process_model.json` の
 * `metadata_extraction_paths`）に頼るしかない。そのキー名や値はモデル依存なので、
 * 判定ロジック側は設定を引数で受け取る形にしてテスト可能にしている。
 *
 * 対象: process-models/sales-forecast/budget-adjustment-approval
 */
export type CaseConfig = {
  /** これらのタスクが開いている＝まだ起案が提出されていない（下書き） */
  readonly draftTaskNames: readonly string[];
  /** 差し戻し判定に使う metadata キー。値が rejectionValue なら差し戻し */
  readonly rejectionMetadataKey: string;
  readonly rejectionValue: string;
  /** 一覧のタイトルに使う metadata キー */
  readonly titleMetadataKey: string;
  /** 一覧で取得する metadata キー（report_metadata.columns に入れる） */
  readonly metadataKeys: readonly string[];
};

/**
 * 件名の metadata キー。**全プロセスモデル共通の予約キー**。
 *
 * 一覧の件名とキーワード検索はこのキーだけを見る。モデルごとに違うキーを許すと
 * 横断一覧が成立しない: 検索は「件名 metadata への contains」で、フィルタは AND で
 * 積まれるため、複数のキー名を対象にした検索を 1 リクエストで表現できない
 * （キーを持たないモデルの案件は INNER JOIN で結果から落ちる）。
 *
 * 各モデルは `process_model.json` の `metadata_extraction_paths` に
 * `{ key: "case_title", path: <件名にする入力項目> }` を宣言する。`path` は自由なので
 * 入力項目名（`request_title` など）はモデルごとに違ってよい。宣言を足しても
 * **既存の案件には遡って作られない**（metadata は実行時に抽出される）点に注意。
 */
export const TITLE_METADATA_KEY = "case_title";

/**
 * 判定の metadata キー。件名と同じく**全モデル共通の予約キー**。
 *
 * 差し戻しの判定はこのキーだけを見る。モデル固有のキー（`manager_decision` など）を
 * 並べると、そのキーを持たないモデルで差し戻しを見落とす（実測: デモ 10 フローは
 * すべて別のキーだったため差し戻しが認識されていなかった）。
 *
 * 段が複数あるフローは**各段が同じ変数へ書く**（最後に判定した人の結果が残る）。
 * 同じ `key` に複数の `path` を宣言する形は使えない: backend の `extract_metadata` は
 * 同一キーを後勝ちで上書きし、path が無い段では None になるため、先の段の判定が
 * 保存されない（`store_metadata` は None を捨てる）。
 * 各段の判定を個別に残したいときは、モデル固有のキーを別途宣言して
 * 「この案件の内容」に出す（判定の導出には使わない）。
 */
export const DECISION_METADATA_KEY = "case_decision";

/**
 * 判定コメント（差し戻し理由）の metadata キー。判定と対で宣言する。
 *
 * 差し戻された人が最初に知りたいのは「なぜ戻ったか」なのに、承認フォームの
 * コメント欄は metadata に出していなかったため画面のどこにも出ていなかった
 * （履歴はイベントログだけで入力値を持たず、task-info はハッシュしか返さない）。
 */
export const DECISION_COMMENT_METADATA_KEY = "case_decision_comment";

export const CASE_CONFIG: CaseConfig = {
  draftTaskNames: ["Activity_Draft"],
  rejectionMetadataKey: DECISION_METADATA_KEY,
  rejectionValue: "差し戻し",
  titleMetadataKey: TITLE_METADATA_KEY,
  metadataKeys: [
    TITLE_METADATA_KEY,
    DECISION_METADATA_KEY,
    DECISION_COMMENT_METADATA_KEY,
    "adjustment_title",
    "amount",
    "manager_decision",
    "director_decision",
  ],
};

/**
 * 案件詳細のデータタブに出す業務データ。キーの順序が表示順になる。
 * ラベルは日本語をここに直接持つ（モデル固有で i18n の対象外）。
 */
/**
 * 案件の内容として詳細に出す項目。ラベルはプロセスモデルのフォーム定義
 * （draft-schema.json 等）の title に合わせる。モデル固有の語（この承認フローの
 * 「申請者」など）はそのまま使う＝画面共通の語彙とは別物。
 */
export const CASE_DATA_FIELDS: readonly {
  readonly key: string;
  readonly label: string;
  readonly format?: "currency";
}[] = [
  // 判定の予約キー。件名（見出しに出る）と違い、ここが唯一の判定表示になる
  { key: "case_decision", label: "判定" },
  { key: "case_decision_comment", label: "判定コメント" },
  { key: "adjustment_title", label: "件名" },
  { key: "amount", label: "調整金額（円）", format: "currency" },
  { key: "reason", label: "理由" },
  { key: "requester_slack_id", label: "申請者 Slack User ID" },
  { key: "manager_decision", label: "部長判定" },
  { key: "manager_comment", label: "部長コメント" },
  { key: "director_decision", label: "事業部長判定" },
  { key: "director_comment", label: "事業部長コメント" },
  { key: "budget_applied", label: "予算反映" },
  { key: "status", label: "処理結果" },
];

/** 再提出（差し戻し案件の内容引き継ぎ）で持ち越す変数。 */
export const CASE_REAPPLY_FIELDS: readonly string[] = [
  "adjustment_title",
  "amount",
  "reason",
  "requester_slack_id",
];
